import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { usePrimaryPropertyId } from "../hooks/useMyProperties";
import { Loader } from "../components/Loader";
import { StatusPill, warrantyState } from "../components/StatusPill";
import { ProgressBar } from "../components/Progress";
import { fmtDate, timeAgo, relativeDays } from "../lib/format";
import { fileUrl } from "../lib/api";
import { Hammer, ArrowRight, ShieldAlert, Wrench, Megaphone, CalendarClock, MapPin } from "lucide-react";

function Card({ children, className = "", ...rest }) {
  return <div className={`bg-arq-card border border-border rounded-md ${className}`} {...rest}>{children}</div>;
}

export default function Dashboard() {
  const { propertyId, isLoading } = usePrimaryPropertyId();
  const navigate = useNavigate();
  const { data, isLoading: dLoading } = useQuery({
    queryKey: ["dashboard", propertyId],
    queryFn: async () => (await api.get(`/properties/${propertyId}/dashboard`)).data,
    enabled: !!propertyId,
  });

  if (isLoading || dLoading) return <Loader label="Loading your home" />;
  if (!propertyId) return <div className="text-center py-20 text-muted-foreground">No property is linked to your account yet. Please contact Arqitek.</div>;

  const p = data.property;
  const hero = p.hero_image ? (p.hero_image.startsWith("http") ? p.hero_image : fileUrl(p.hero_image)) : "https://images.pexels.com/photos/8134821/pexels-photo-8134821.jpeg";

  return (
    <div className="space-y-6 arq-fade-up" data-testid="homeowner-dashboard">
      {/* Hero */}
      <Card className="overflow-hidden">
        <div className="relative h-56 sm:h-72">
          <img src={hero} alt={p.address} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
          <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8 text-white">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-xs bg-arq-card/20 backdrop-blur px-2 py-0.5 rounded-sm">#{p.residence_id}</span>
              <StatusPill status={p.status === "Under Construction" ? "In Progress" : "Active"} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl">{p.address}</h1>
            <p className="flex items-center gap-1.5 text-white/80 text-sm mt-1"><MapPin className="h-3.5 w-3.5" />{p.community} · {p.city}, {p.state}</p>
          </div>
        </div>
        {p.status === "Under Construction" && (
          <div className="p-6 sm:p-8 grid sm:grid-cols-[1fr_auto] gap-6 items-center">
            <div className="w-full">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-arq-ink flex items-center gap-2"><Hammer className="h-4 w-4 text-arq-accent" /> Construction Progress</span>
                <span className="font-display text-2xl text-arq-ink">{p.completion_percentage}%</span>
              </div>
              <ProgressBar value={p.completion_percentage} />
              <div className="flex items-center gap-6 mt-4 text-sm">
                {data.current_phase && <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Current Phase</p><p className="font-medium text-arq-ink">{data.current_phase.name}</p></div>}
                {data.next_phase && <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Next Phase</p><p className="font-medium text-arq-ink">{data.next_phase.name}</p></div>}
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Est. Completion</p><p className="font-medium text-arq-ink">{fmtDate(p.estimated_completion)}</p></div>
              </div>
            </div>
            <Link to="/build" className="inline-flex items-center gap-2 text-sm font-medium text-arq-green hover:gap-3 transition-all" data-testid="view-build-link">
              View Build Progress <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </Card>

      {/* Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent updates */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl flex items-center gap-2"><Megaphone className="h-4 w-4 text-arq-accent" /> Recent Updates</h2>
            <Link to="/updates" className="text-xs text-arq-accent hover:underline">View all</Link>
          </div>
          <div className="space-y-4">
            {data.recent_updates.length === 0 && <p className="text-sm text-muted-foreground">No updates yet.</p>}
            {data.recent_updates.slice(0, 3).map((u) => (
              <div key={u.id} className="border-l-2 border-arq-surface pl-4" data-testid={`dash-update-${u.id}`}>
                <p className="text-sm font-medium text-arq-ink">{u.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{u.message}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{timeAgo(u.date)}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Service */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl flex items-center gap-2"><Wrench className="h-4 w-4 text-arq-accent" /> Service Requests</h2>
            <Link to="/service" className="text-xs text-arq-accent hover:underline">View all</Link>
          </div>
          {data.open_service_requests.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open service requests. <Link to="/service/new" className="text-arq-accent hover:underline">Submit one</Link>.</p>
          ) : (
            <div className="space-y-3">
              {data.open_service_requests.slice(0, 3).map((s) => (
                <button key={s.id} onClick={() => navigate(`/service/${s.id}`)} className="w-full flex items-center justify-between gap-2 text-left hover:bg-arq-surface/50 -mx-2 px-2 py-1.5 rounded-md" data-testid={`dash-sr-${s.id}`}>
                  <div>
                    <p className="text-sm font-medium text-arq-ink">#{s.number} · {s.category}</p>
                    <p className="text-xs text-muted-foreground">{s.location}</p>
                  </div>
                  <StatusPill status={s.status} />
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Appointments */}
        <Card className="p-6">
          <h2 className="font-display text-xl flex items-center gap-2 mb-4"><CalendarClock className="h-4 w-4 text-arq-accent" /> Upcoming Appointments</h2>
          {(!data.upcoming_appointments || data.upcoming_appointments.length === 0) ? (
            <p className="text-sm text-muted-foreground">No appointments scheduled.</p>
          ) : (
            <div className="space-y-3">
              {data.upcoming_appointments.map((s) => (
                <div key={s.id} className="flex items-center justify-between" data-testid={`dash-appt-${s.id}`}>
                  <div>
                    <p className="text-sm font-medium text-arq-ink">{fmtDate(s.appointment.date)} · {s.appointment.start_time}</p>
                    <p className="text-xs text-muted-foreground">SR #{s.number} · {s.category}</p>
                  </div>
                  <StatusPill status="Scheduled" />
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Expiring warranties */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-arq-accent" /> Warranty Alerts</h2>
            <Link to="/warranty" className="text-xs text-arq-accent hover:underline">View all</Link>
          </div>
          {data.expiring_warranties.length === 0 ? (
            <p className="text-sm text-muted-foreground">No warranties expiring soon. You're covered.</p>
          ) : (
            <div className="space-y-3">
              {data.expiring_warranties.map((w) => (
                <div key={w.id} className="flex items-center justify-between" data-testid={`dash-warranty-${w.id}`}>
                  <div>
                    <p className="text-sm font-medium text-arq-ink">{w.name}</p>
                    <p className="text-xs text-muted-foreground">Expires {relativeDays(w.expiration_date)}</p>
                  </div>
                  <StatusPill status={warrantyState(w.expiration_date)} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
